import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { useRef } from 'react';
import { uuid } from '@/lib/uuid';
import { isApiError } from './errors';

/**
 * Should a failed attempt keep its Idempotency-Key? Only when the outcome is
 * unknown (network, 5xx, rate limit, still in flight). Any definitive answer
 * — success or a 4xx — is stored by the API against the key, so the next
 * submission (likely with corrected input) needs a fresh one, or it would be
 * rejected as IDEMPOTENCY_KEY_REUSED.
 */
export function keepsIdempotencyKey(error: unknown): boolean {
  return isApiError(error) && error.isRetryableWithSameKey;
}

type IdempotentOptions<TData, TVariables, TContext> = Omit<
  UseMutationOptions<TData, Error, TVariables, TContext>,
  'mutationFn' | 'retry' | 'retryDelay'
> & {
  mutationFn: (variables: TVariables, idempotencyKey: string) => Promise<TData>;
  /** Automatic retries with the same key while the outcome is unknown (default 2). */
  retries?: number;
};

/**
 * A mutation for creation and money-moving commands. One UUID per user
 * action: it is reused for automatic and manual retries of the same
 * submission and rotated after a definitive outcome.
 */
export function useIdempotentMutation<TData, TVariables = void, TContext = unknown>(
  options: IdempotentOptions<TData, TVariables, TContext>,
) {
  const keyRef = useRef<string | null>(null);
  const { mutationFn, retries = 2, onSettled, ...rest } = options;

  return useMutation<TData, Error, TVariables, TContext>({
    ...rest,
    mutationFn: (variables) => {
      keyRef.current ??= uuid();
      return mutationFn(variables, keyRef.current);
    },
    retry: (failureCount, error) => failureCount < retries && keepsIdempotencyKey(error),
    retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 4000),
    onSettled: (...args) => {
      const error = args[1];
      if (!keepsIdempotencyKey(error)) keyRef.current = null;
      return onSettled?.(...args);
    },
  });
}
